import { Button } from "@/components/ui/button";
import { ArrowRight, BookOpen, Users, Heart } from "lucide-react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};

export const CallToActionSection = ({ studentLink, educatorLink, showTeacher = false }: { studentLink?: string, educatorLink?: string, showTeacher?: boolean }) => {
  const navigate = useNavigate();
  const actions = [
    {
      icon: <BookOpen className="w-5 h-5 justify-center" />,
      title: "Register as a Student",
      description: "Begin your Python journey",
      cta: "Register Now",
      variant: "hero" as const,
      method: () => {
        window.open(studentLink || '#', '_blank');
      }
    },
    ...(showTeacher ? [{
      icon: <Users className="w-5 h-5 justify-center" />,
      title: "Register as an Educator",
      description: "Teach the skills of the future",
      cta: "Register Now",
      variant: "outline" as const,
      method: () => {
        window.open(educatorLink || '#', '_blank');
      }
    }] : []),
    {
      icon: <Heart className="w-5 h-5 justify-center" />,
      title: "Start a Chapter",
      description: "Bring Byte-Sized Python to your school or community",
      cta: "Get Started",
      variant: "outline" as const,
      method: () => {
        navigate("/start-a-chapter");
      }
    },
  ];

  return (
    <section className="relative pt-12 md:pt-16 pb-24 overflow-hidden">
      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="max-w-5xl mx-auto text-center space-y-16"
        >
          {/* Header */}
          <motion.div variants={item} className="space-y-6">
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-foreground">
              Start Your <span className="text-primary">Coding Journey</span>
            </h2>
            <motion.div
              variants={item}
              className="h-0.5 bg-gradient-to-r from-primary/30 to-transparent w-24 mx-auto"
            />
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed max-w-3xl mx-auto">
              Whether you're a student, educator, or supporter, join our community and be part of the next generation of coders.
            </p>
          </motion.div>

          {/* Action Cards */}
          <div className={`grid gap-6 ${actions.length >= 3 ? 'md:grid-cols-2 lg:grid-cols-3' : actions.length === 2 ? 'md:grid-cols-2' : ''}`}>
            {actions.map((action) => (
              <motion.div 
                key={action.title}
                variants={item}
                className="group bg-white/90 backdrop-blur-sm rounded-xl p-8 border border-border shadow-card hover:shadow-hero transition-all flex flex-col"
              >
                <div className="flex-1 space-y-6">
                <div className="flex items-center justify-center">
                    <div className="flex items-center justify-center bg-secondary/30 h-14 w-14 rounded-full text-foreground">
                      {action.icon}
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <h3 className="text-xl font-medium text-foreground">
                      {action.title}
                    </h3>
                    <p className="text-muted-foreground">
                      {action.description}
                    </p>
                  </div>
                </div>

                <Button 
                  variant={action.variant}
                  size="lg"
                  className="mt-6 w-full"
                  onClick={action.method}
                >
                  {action.cta}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};